import React, { useEffect, useState } from 'react';
import { useSearchParams,  } from 'react-router-dom';
import DashboardLayout from '../../DashboardLayout';
import { ALCProvider, useALCContext, ALC_STATES, } from './context/ALCContext';
import ALCInitProduction from './components/ALCInitProduction';
import ALCPendingQA from './components/ALCPendingQA';
import ALCChecklist from './components/ALCChecklist';
import ALCProductionAction from './components/ALCProductionAction';
import ALCQAReverify from './components/ALCQAReverify';
import ALCSummary from './components/ALCSummary';
import FullPageLoader from '../../common/FullPageLoader';
import { alcService } from './services/alcService';

import moment from 'moment';

const ALCMain: React.FC = () => {
    const { state, dispatch } = useALCContext();
    const [searchParams] = useSearchParams();
    const [isResuming, setIsResuming] = useState(false);

    useEffect(() => {
        const tourId = searchParams.get('id') || searchParams.get('TourId') || searchParams.get('tourId') || searchParams.get('tourid');
        if (!tourId) {
            // Fresh new request: clear previous state and stay at INIT_PRODUCTION
            dispatch({ type: 'SET_STATE', payload: ALC_STATES.INIT_PRODUCTION });
            dispatch({ type: 'SET_TOUR_ID', payload: '' });
            dispatch({ type: 'SET_READ_ONLY', payload: false });
            dispatch({ type: 'SET_PREVIOUS_DAY', payload: false });
            return; 
        }

        // Resume existing session — mirrors main.js resumeSessionState()
        const resumeSession = async () => {
            setIsResuming(true);
            try {
                const session = await alcService.getSessionById(tourId);
                const status: string = (session.cr3ea_processstatus || session.cr3ea_status || "").trim();

                // Check same day observation
                const creationTime = session.createdon || session.cr3ea_tourstartdate;
                let isToday = true;
                if (creationTime) {
                    const parsed = moment(creationTime);
                    if (parsed.isValid()) {
                        isToday = (parsed.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD"));
                    }
                }
                const isPreviousDay = !isToday;
                dispatch({ type: 'SET_PREVIOUS_DAY', payload: isPreviousDay });

                // Parse the cr3ea_observedby field: "ProdExec || Shift || Line || PrevProduct || NewProduct"
                const observedBy: string = session.cr3ea_observedby || "";
                let prodExec = session.cr3ea_shiftexecutiveproduction || "";
                let shift = session.cr3ea_shift || "";
                let line = session.cr3ea_lineno || "";
                let prevProduct = session.cr3ea_previousrunningvariety || "";
                let newProduct = session.cr3ea_runningvariety || "";

                if (observedBy.includes("||")) {
                    const parts = observedBy.split("||");
                    prodExec = parts[0]?.trim() || prodExec;
                    shift = parts[1]?.trim() || shift;
                    line = parts[2]?.trim() || line;
                    prevProduct = parts[3]?.trim() || prevProduct;
                    newProduct = parts[4]?.trim() || newProduct;
                } else if (observedBy) {
                    prodExec = observedBy;
                }

                const assignedQaEmail = session.cr3ea_assigned_qa || "";
                const assignedQaName = session.cr3ea_tourby || session.cr3ea_shiftexecutivequality || "";
                const requestTime = session.cr3ea_tourstartdate || session.cr3ea_request_time || null;
                const escalationEmailsStr = session.cr3ea_escalation_contacts || "";
                const escalationEmails = escalationEmailsStr ? escalationEmailsStr.split(',') : [];

                // Restore session data into context
                dispatch({ type: 'SET_TOUR_ID', payload: tourId });
                dispatch({
                    type: 'SET_SESSION_DATA',
                    payload: {
                        plant: 'Rajpura',
                        line,
                        shift,
                        productionExecutive: prodExec,
                        previousProduct: prevProduct,
                        newProduct,
                        assignedQaEmail,
                        assignedQaName,
                        escalationEmails,
                        requestTime,
                        rawRecord: session
                    }
                });

                // Load checkpoints to support routing
                let checkpoints: any[] = [];
                try {
                    checkpoints = await alcService.getCheckpoints(tourId);
                    const failed = checkpoints.filter((c: any) => 
                        c.cr3ea_status === "Not Okay" || 
                        (c.cr3ea_defectcategory && (
                            c.cr3ea_defectcategory.includes("00") || 
                            c.cr3ea_defectcategory.includes("01") || 
                            c.cr3ea_defectcategory.includes("Non-Compliant") || 
                            c.cr3ea_defectcategory.includes("Partial")
                        ))
                    );
                    dispatch({ type: 'SET_FAILED_CHECKPOINTS', payload: failed });
                } catch (e) {
                    console.warn("Could not load checkpoints for resume:", e);
                }

                // Status-based state routing mirroring main.js transitionByStatus()
                const lowerStatus = status.toLowerCase();
                if (lowerStatus === "completed" || lowerStatus === "closed" || lowerStatus === "success") {
                    dispatch({ type: 'SET_READ_ONLY', payload: true });
                    dispatch({ type: 'SET_STATE', payload: ALC_STATES.SUMMARY });
                } else if (lowerStatus === "closed - expired" || (isPreviousDay && lowerStatus.includes("expired"))) {
                    // Check if production has pending actions
                    const hasPendingActions = checkpoints.some(c => 
                        (c.cr3ea_status === "Not Okay" || (c.cr3ea_defectcategory && (c.cr3ea_defectcategory.includes("Non-Compliant") || c.cr3ea_defectcategory.includes("Partial")))) &&
                        (!c.cr3ea_productionremarks && (!c.cr3ea_defectremarks || !c.cr3ea_defectremarks.trim().startsWith("Action:")))
                    );
                    if (state.userRole !== "QUALITY" && hasPendingActions) {
                        dispatch({ type: 'SET_READ_ONLY', payload: false });
                        dispatch({ type: 'SET_STATE', payload: ALC_STATES.PRODUCTION_ACTION });
                    } else {
                        dispatch({ type: 'SET_READ_ONLY', payload: true });
                        dispatch({ type: 'SET_STATE', payload: ALC_STATES.SUMMARY });
                    }
                } else if (lowerStatus === "pending qa" || lowerStatus === "escalated") {
                    dispatch({ type: 'SET_READ_ONLY', payload: false });
                    dispatch({ type: 'SET_STATE', payload: ALC_STATES.PENDING_QA_ACCEPTANCE });
                } else if (lowerStatus === "qa in progress" || lowerStatus.startsWith("qa in progress")) {
                    dispatch({ type: 'SET_READ_ONLY', payload: false });
                    dispatch({ type: 'SET_STATE', payload: ALC_STATES.QA_CHECKLIST });
                } else if (lowerStatus.includes("pending production")) {
                    dispatch({ type: 'SET_READ_ONLY', payload: false });
                    dispatch({ type: 'SET_STATE', payload: ALC_STATES.PRODUCTION_ACTION });
                } else if (lowerStatus.includes("pending re-verification")) {
                    dispatch({ type: 'SET_READ_ONLY', payload: false });
                    dispatch({ type: 'SET_STATE', payload: ALC_STATES.QA_REVERIFYING });
                } else {
                    dispatch({ type: 'SET_STATE', payload: ALC_STATES.INIT_PRODUCTION });
                }

            } catch (error: any) {
                console.error("Failed to resume session:", error);
                alert("Could not load the ALC session. It may have been deleted or the ID is incorrect.\n" + error.message);
            } finally {
                setIsResuming(false);
            }
        };

        resumeSession();
    }, [searchParams, state.userRole, dispatch]);


    const renderCurrentState = () => {
        if (isResuming) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading ALC session...</p>
                </div>
            );
        }

        switch (state.currentState) {
            case ALC_STATES.INIT_PRODUCTION:
                return <ALCInitProduction />;
            case ALC_STATES.PENDING_QA_ACCEPTANCE:
                return <ALCPendingQA />;
            case ALC_STATES.QA_CHECKLIST:
                return <ALCChecklist />;
            case ALC_STATES.PRODUCTION_ACTION:
                return <ALCProductionAction />;
            case ALC_STATES.QA_REVERIFYING:
                return <ALCQAReverify />;
            case ALC_STATES.SUMMARY:
                return <ALCSummary />;
            case ALC_STATES.COMPLETED_PASS:
                setTimeout(() => dispatch({ type: 'SET_STATE', payload: ALC_STATES.SUMMARY }), 0);
                return null;
            default:
                return <ALCInitProduction />;
        }
    };

    return (
        <DashboardLayout>
            <FullPageLoader isLoading={state.isLoading} message="Processing ALC request... Please wait." />
            <main id="main" className="main">
                <div className="container-fluid" style={{ padding: '20px' }}>
                    <div className="row">
                        <div className="col-12 text-center mb-4">
                            <h2 style={{ fontWeight: 'bold', color: '#1f2937' }}>QUALITY ALC CHECKLIST (Rajpura)</h2>
                            <p className="text-muted">AIB | Quality | Rajpura Plant</p>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            {renderCurrentState()}
                        </div>
                    </div>
                </div>
            </main>
        </DashboardLayout>
    );
};

const ALC: React.FC = () => {
    return (
        <ALCProvider>
            <ALCMain />
        </ALCProvider>
    );
};

export default ALC;
